/**
 * `requireModule` — the Layer-3 route guard of the 4-layer module gate.
 *
 * Returns a TanStack `beforeLoad` that redirects to `/` when the active
 * workspace isn't entitled to `module`. This is what makes a typed URL to a
 * hidden module actually fail: nav (Layer 2, `nav-config.ts`) removes the link,
 * `entitledProcedureFor` (Layer 4, `apps/api/src/trpc.ts`) refuses the data, and
 * this guard stops the page component from ever mounting — a clean redirect home
 * instead of a half-rendered shell that errors on every query (the "half-hidden
 * module is worse than a visible one" failure the release plan warns about).
 *
 * It reads `modules` from the cached `Me` (`meQueryOptions`, already hydrated by
 * the root route's own `beforeLoad`), so it's a pure in-memory check with no
 * extra round-trip. `.catch(() => null)` mirrors the existing permission guards
 * (`settings/roles`): if the session fails to load we fall through to `/`, where
 * the root route's auth redirect takes over, rather than throwing here.
 *
 * Redirect (not `notFound()`) is deliberate — the 1.0 gate is "a typed
 * `/payroll` URL redirects home."
 *
 * @see docs/adr/0046-withheld-1.0-modules.md
 * @see docs/product/v1.0-release-plan.md §3 (the four layers)
 */
import { redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { AppModule } from "@flint/api-contract";
import { meQueryOptions } from "../../../lib/session.ts";

export function requireModule(module: AppModule) {
  return async ({ context }: { context: { queryClient: QueryClient } }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const modules = me?.company?.modules ?? [];
    if (!modules.includes(module)) {
      throw redirect({ to: "/" });
    }
  };
}
